import { Button, Col, Flex, Input, Layout, List, notification, Space, Splitter, Row, Skeleton, InputRef } from 'antd';
import { useEffect, useRef, useState, useMemo, ReactElement } from "react";
import MenuOptionEditorDrawer from './menuoptioneditordrawer';
import MenuOptionPropertyEditor from './menuoptionpropertyeditor';
import MenuOptionGroupOptions from './menuoptiongroupoptions';
import MenuItemsForGroup from './menuitemsforgroup';
import {
  getAllGroups, getGroupById, getGroupOptions, getMenuItemsForGroup,
  getMenuOptionsDropdown, createGroup, updateGroup, deleteGroup,
  saveOptionLink, updateOptionLink, deleteOptionLink, updateSortOrder,
  addMenuOptionGroupToMenu, removeMenuOptionGroupFromMenu
} from '../../services/menuoptiongroupservice';
import { getOptionTableColumns } from './menuoptiontablecolumns';
import {useAuth} from "../../auth/authcontext";
// dnd-kit
import React, { useContext } from 'react';
import { HolderOutlined } from '@ant-design/icons';
import { KeyboardSensor, DragEndEvent, PointerSensor, PointerSensorOptions, useSensor, useSensors, DraggableAttributes } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates, useSortable, } from "@dnd-kit/sortable";
import { MenuOptionGroup, MenuOptionGroupOption, MenuItem, MenuOptionDropdown, DragRowProps } from '../../types/types';
import { CSS } from '@dnd-kit/utilities';
import { StoresListToPublish } from '../../types/storetypes';
import { getAllStores } from '../../services/storeserivce';
import dayjs, { Dayjs } from 'dayjs';

interface RowContextType {
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  listeners: DraggableAttributes;
}

type NotificationType = "success" | "info" | "warning" | "error";

export const MenuOptions = () => {
  const [menuOptionGroups, setMenuOptionGroups] = useState<MenuOptionGroup[]>([]);
  const [menuOptionGroupsFilter, setMenuOptionGroupsFilter] = useState('');
  const menuOptionGroupsFiltered = useMemo(() => {
    const lower = menuOptionGroupsFilter.trim().toLowerCase();
    if (!lower) return menuOptionGroups;
    return menuOptionGroups.filter((mog) =>
      (mog.name || "").toLowerCase().includes(lower) ||
      (mog.displayName || "").toLowerCase().includes(lower)
    );
  }, [menuOptionGroups, menuOptionGroupsFilter]);
  const [selectedMogName, setSelectedMogName] = useState('');
  const [selectedMogDisplayName, setSelectedMogDisplayName] = useState('');
  const [selectedMogMinSelect, setSelectedMogMinSelect] = useState<number | null>(null);
  const [selectedMogMaxSelect, setSelectedMogMaxSelect] = useState<number | null>(null);
  const [selectedMogGuid, setSelectedMogGuid] = useState('');
  const [selectedMogOptions, setSelectedMogOptions] = useState<MenuOptionGroupOption[]>([]);
  const [selectedMogModifier, setSelectedMogModifier] = useState('');
  const [selectedMogIsOnSale, setSelectedMogIsOnSale] = useState(false);
  const [menuOptionsDropDown, setmenuOptionsDropDown] = useState<MenuOptionDropdown[]>([]);
  const [selectedMenuOptionGuid, setSelectedMenuOptionGuid] = useState('');
  const [selectedOptionDisplayName, setSelectedOptionDisplayName] = useState('');
  const [selectedOptionDefaultSelection, setSelectedOptionDefaultSelection] = useState<boolean>(false);
  const [selectedOptionStartDate, setSelectedOptionStartDate] = useState<Dayjs | null>(null);
  const [selectedOptionEndDate, setSelectedOptionEndDate] = useState<Dayjs | null>(null);
  const [openEditMogmol, setopenEditMogmol] = useState(false);
  const [selectedOptionGuid, setSelectedOptionGuid] = useState('');
  const [isAddOptionButtonDisabled, setIsAddOptionButtonDisabled] = useState(true);
  const inputRefGroupName = useRef<InputRef>(null!);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isWarnGroupName, setIsWarnGroupName] = useState(false);
  const [isWarnGroupDisplayName, setIsWarnGroupDisplayName] = useState(false);
  const [isWarnOptionDisplayName, setIsWarnOptionDisplayName] = useState(false);
  const [isWarnMenuOption, setIsWarnMenuOption] = useState(false);
  const [menuItemsForGroup, setMenuItemsForGroup] = useState<MenuItem[]>([]);
  const [isChangeMenuOptionGroup, setIsChangeMenuOptionGroup] = useState('load');
  const [isShowMenuOptionGroupProperties, setIsShowMenuOptionGroupProperties] = useState<boolean>(false);
  const [stores, setStores] = useState<StoresListToPublish[]>([]);
  const [selectedStoreGuid, setSelectedStoreGuid] = useState<string | null>(null);
  const { userId: userGuid, isReadOnly } = useAuth();

  useEffect(() => {
    if (isChangeMenuOptionGroup !== '') {
      bindOptionGroups();
    }
  }, [isChangeMenuOptionGroup]);

  const bindOptionGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await getAllGroups();
      setMenuOptionGroups(response.data);
    } catch (error) {
      console.error("Error loading groups", error);
    } finally {
      setIsLoadingGroups(false);
      setIsChangeMenuOptionGroup('');
    }
  };

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await getAllStores();
        setStores(response);
        if (response.length > 0) {
          setSelectedStoreGuid(response[0].guid); // default store
        }
      } catch (error) {
        console.error("Error fetching stores", error);
      }
    };
    fetchStores();
  }, []);

  const bindOptionsTable = async (id: string, storeGuid: string): Promise<void> => {
    try {
      const response = await getGroupOptions(id, storeGuid);
      const mogmols = response.data.map((item: any) => ({
        key: item.guid,
        menuOptionGroupGuid: item.menuOptionGroupGuid,
        menuGuid: item.menuGuid,
        menuOptionName: item.menu.name,
        displayName: item.displayName,
        sortOrder: item.sortOrder,
        defaultSelection: item.defaultSelection,
        actionListItemGuid: item.actionListItemGuid,
        optionImageName: item.menu.imageName,
        price: item.price,
        startDate: item.startDate ? item.startDate : null,
        endDate: item.endDate ? item.endDate : null,
        active: item.active,
      }));
      setSelectedMogOptions(mogmols);
    } catch (error) {
      console.error("Error loading group options", error);
    }
  };

  const bindMenuIemsForGroup = async (id: string): Promise<void> => {
    try {
      const response = await getMenuItemsForGroup(id);
      setMenuItemsForGroup(response.data as MenuItem[]);
    } catch (error) {
      console.error("Error loading menu items for group", error);
    }
  };

  // when a user clicks on a Menu Option Group, load the group properties and any assigned options in Group Options Card
  const handleClickLoadMog = async (id: string): Promise<void> => {
    const mogProps = await getGroupById(id).then(res => res.data);
    if (mogProps) {
      setSelectedMogName(mogProps.name);
      setSelectedMogDisplayName(mogProps.displayName);
      setSelectedMogMinSelect(mogProps.minSelect);
      setSelectedMogMaxSelect(mogProps.maxSelect);
      setSelectedMogGuid(mogProps.guid);
      setSelectedMogModifier(mogProps.modifier ? mogProps.modifier : '');
      setSelectedMogIsOnSale(!!mogProps.isOnSale);
      if (selectedStoreGuid) {
        await bindOptionsTable(id, selectedStoreGuid);
      }
      await bindMenuIemsForGroup(id);
      setIsShowMenuOptionGroupProperties(true); // better as boolean
    }
    bindMenuOptions();
    setIsAddOptionButtonDisabled(false);
  };

  // get menu items for drop down
  const bindMenuOptions = async (): Promise<void> => {
    if (menuOptionsDropDown.length === 0) {
      try {
        const response = await getMenuOptionsDropdown();
        const options: MenuOptionDropdown[] = response.data.map((item: any) => ({
          key: item.guid,
          name: item.name,
          upc: item.upc,
          imageName: item.imageName,
          itemType: item.itemType,
        }));
        setmenuOptionsDropDown(options);
      } catch (error) {
        console.error("Error loading menu options dropdown", error);
      }
    }
  };

  const handleChangeMinSelect = (value: number | null): void => {
    setSelectedMogMinSelect(value);
  };

  const handleChangeMaxSelect = (value: number | null): void => {
    setSelectedMogMaxSelect(value);
  };

  const handleChangeModifier = (value: string): void => {
    setSelectedMogModifier(value);
  };

  const handleSaveMenuOptionGroup = async () => {
    let isvalid = true;
    if (selectedMogName === '') {
      setIsWarnGroupName(true);
      isvalid = false;
    }
    if (selectedMogDisplayName === '') {
      setIsWarnGroupDisplayName(true);
      isvalid = false;
    }

    if (!isvalid) return;

    const payload = {
      Name: selectedMogName,
      DisplayName: selectedMogDisplayName,
      MinSelect: selectedMogMinSelect ?? 0,
      MaxSelect: selectedMogMaxSelect ?? 0,
      Modifier: selectedMogModifier,
      Active: true,
      IsOnSale: selectedMogIsOnSale,
    };


    try {
      let response;
      if (selectedMogGuid === '') {
        response = await createGroup(payload);
        if (response.status === 201) {
          showNotification("success", "Success", "Group Created");
          setSelectedMogGuid(response.data.guid);
        }
      } else {
        response = await updateGroup({ ...payload, Guid: selectedMogGuid });
        if (response.status === 200) {
          showNotification("success", "Success", "Group Properties Updated");
        }
      }
    } catch (error) {
      console.error('Error saving group', error);
      showNotification("error", "Error", "Error Saving Group");
    } finally {
      bindOptionGroups();
      setIsAddOptionButtonDisabled(false);
    }
  };

  const handleDeleteMenuOptionGroup = async () => {
    try {
      const response = await deleteGroup(selectedMogGuid);
      if (response.status === 200) {
        showNotification("success", "Success", "Option Deleted");
        resetGroupProperties();
      }
    } catch (error) {
      console.error('Error deleting group', error);
      showNotification("error", "Error", "Error Deleting Option");
    } finally {
      bindOptionGroups();
    }
  };

  const resetGroupProperties = () => {
    setSelectedMogGuid('');
    setSelectedMogName('');
    setSelectedMogDisplayName('');
    setSelectedMogMinSelect(0);
    setSelectedMogMaxSelect(0);
    setSelectedMogModifier('');
    setSelectedMogIsOnSale(false);
    //show section
    setIsShowMenuOptionGroupProperties(true);
    //clear group options
    setSelectedMogOptions([]);
    // clear  menu items
    setMenuItemsForGroup([]);
    // get menu items for drop down if it wasn't already populated
    bindMenuOptions();
    //set focus
    if (inputRefGroupName.current) {
      inputRefGroupName.current.focus();
    }
  }

  const showNotification = (
    type: NotificationType,
    msg: string,
    dscr: string
  ): void => {
    notification[type]({
      message: msg,
      description: dscr,
      duration: 3,
    });
  };


  const handleSaveOptionLink = async () => {
    let isvalid = true;

    if (selectedOptionDisplayName === '') {
      setIsWarnOptionDisplayName(true);
      isvalid = false;
    }

    if (selectedMenuOptionGuid === '' || selectedMenuOptionGuid === '(SELECT)') {
      setIsWarnMenuOption(true);
      isvalid = false;
    }

    if (!isvalid) return;

    const payload = {
      MenuOptionGroupGuid: selectedMogGuid,
      MenuGuid: selectedMenuOptionGuid,
      DisplayName: selectedOptionDisplayName,
      SortOrder: 1, // or whatever order you want to assign
      DefaultSelection: selectedOptionDefaultSelection, // make sure this is boolean
      ActionListItemGuid: undefined, // optional
      StartDate: selectedOptionStartDate ? selectedOptionStartDate : null,
      EndDate: selectedOptionEndDate ? selectedOptionEndDate : null,
      Active: true,
      CreatedBy: userGuid ?? ''
    };

    try {
      const response = selectedOptionGuid === ''
        ? await saveOptionLink(payload)
        : await updateOptionLink({ ...payload, Guid: selectedOptionGuid });

      if (response.status === 200) {
        showNotification("success", "Success", "Option Properties Updated");
      }
    } catch (error) {
      console.error('Error updating the menu option!', error);
      showNotification("error", "Error", "Error Updating Option Properties");
    } finally {
      setopenEditMogmol(false);
      if (selectedStoreGuid) {
        bindOptionsTable(selectedMogGuid, selectedStoreGuid);
      }
    }
  };

  const handleDeleteOption = async (
    id: string,
    event?: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    try {
      const response = await deleteOptionLink(id);
      if (response.status === 200) {
        notification.success({
          message: "Success",
          description: "Option Deleted",
        });
      }
    } catch (error) {
      console.error("There was an error deleting option!", error);
      notification.error({
        message: "Error",
        description: "Error Deleting Option",
      });
    } finally {
      if (selectedStoreGuid) {
        bindOptionsTable(selectedMogGuid, selectedStoreGuid);
      }
      bindMenuIemsForGroup(selectedMogGuid);
    }
  };

  const handleCancelOptionLink = () => {
    // reset values and close edit drawer
    resetOptions();
    setopenEditMogmol(false);
  }

  const resetOptions = () => {
    setSelectedOptionGuid('');
    setSelectedOptionDisplayName('');
    setSelectedMenuOptionGuid('');
    setSelectedOptionDefaultSelection(false);
  }

  const handleOptionDisplayTextChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedOptionDisplayName(e.target.value);
    if (e.target.value !== '') {
      setIsWarnOptionDisplayName(false);
    }
  };

  const handleOptionChange = (value: string): void => {
    setSelectedMenuOptionGuid(value);
    if (value !== '') {
      setIsWarnMenuOption(false);
    }
  };

  const handleDefaultOptionChange = (value: boolean): void => {
    setSelectedOptionDefaultSelection(value);
  };

  const handleOptionStartDateChange = (date: Dayjs | null) => {
      setSelectedOptionStartDate(date ? date : null);
  };

  const handleOptionEndDateChange = (date: Dayjs | null) => {
    setSelectedOptionEndDate(date ? date : null);
  };


  const handleNewMenuOption = () => {
    resetOptions();
    setopenEditMogmol(true);
  }

  // Group Name
  const handleMogNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedMogName(e.target.value);
    if (e.target.value !== '') {
      setIsWarnGroupName(false);
    }
  };

  //Group Display Text
  const handleMogDisplayNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedMogDisplayName(e.target.value);
    if (e.target.value !== '') {
      setIsWarnGroupDisplayName(false);
    }
  };
  //closing edit drawer
  const onEditDrawerClose = () => {
    setopenEditMogmol(false);
  };

  // draggable rows
  const RowContext = React.createContext<RowContextType | null>(null);
  const DragHandle = (): ReactElement => {
    const rowContext = useContext(RowContext);

    // if rowContext is missing, render a harmless fallback
    if (!rowContext) {
      return <span style={{ visibility: "hidden" }}>::</span>;
    }

    const { setActivatorNodeRef, listeners } = rowContext;

    return (
      <Button
        type="text"
        ref={setActivatorNodeRef}
        {...listeners}
      >
        ::
      </Button>
    );
  };


  // extend PointerSensor to ignore button click, otherwise action links in row do not work
  class MyPointerSensor extends PointerSensor {
    static activators: typeof PointerSensor.activators = [
      {
        eventName: "onPointerDown" as const,
        handler: (
          { nativeEvent: event }: any,
          { onActivation }: PointerSensorOptions
        ): boolean => {
          if (
            event.target instanceof HTMLButtonElement ||
            event.target instanceof HTMLAnchorElement
          ) {
            return false;
          }
          return true;
        },
      },
    ];
  }

  // dnd kit sensors, had to extend PointerSensor to ignore button click so Edit and Delete links work
  const sensors = useSensors(
    useSensor(MyPointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async ({ active, over }: DragEndEvent): Promise<void> => {
    if (over && active.id !== over.id) {
      const oldIndex = selectedMogOptions.findIndex(item => item.key === active.id);
      const newIndex = selectedMogOptions.findIndex(item => item.key === over.id);

      const updatedData = arrayMove(selectedMogOptions, oldIndex, newIndex);

      if (updatedData.length > 0) {
        const sortValues: Record<string, number> = {};

        for (let i = 0; i < updatedData.length; i++) {
          updatedData[i].sortOrder = i + 1;
          sortValues[updatedData[i].key] = i + 1; // key is Guid
        }

        setSelectedMogOptions(updatedData);

        updateSortOrder(sortValues)
          .catch(error => {
            console.error("Error updating sort order:", error);
            alert("Failed to update sort order. Please try again.");
          });
      }
    }
  };

  const DragRow: React.FC<DragRowProps> = (props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: props["data-row-key"],
    });

    // Merge AntD's style with our drag styles
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      cursor: "move",
      ...(props.style || {}), // merge AntD's style last so width/height etc. are preserved
    };

    const childrenWithHandle = React.Children.map(props.children, (child, index) => {
      if (index === 0 && React.isValidElement(child)) {
        return React.cloneElement(child as React.ReactElement<any>, {
          children: (
            <span className="drag-handle" {...listeners} {...attributes}>
              <HolderOutlined />
            </span>
          ),
        });
      }
      return child;
    });

    return (
      <tr ref={setNodeRef} style={style}>
        {childrenWithHandle}
      </tr>
    );
  };

  const columnsSort = getOptionTableColumns({
    DragHandle,
    setopenEditMogmol,
    setSelectedOptionDisplayName,
    setSelectedMenuOptionGuid,
    setSelectedOptionDefaultSelection,
    setSelectedOptionStartDate,
    setSelectedOptionEndDate,
    setSelectedOptionGuid,
    handleDeleteOption,
    isReadOnly,
  });

  const handleAddMenuItem = async (menuItemGuid: string) => {
    try {
      await addMenuOptionGroupToMenu({
        menuItemId: menuItemGuid,
        menuOptionGroupGuid: selectedMogGuid,
        createdByGuid: userGuid ?? "",
      });
      showNotification("success", "Success", "Menu Item assigned successfully");
      await bindMenuIemsForGroup(selectedMogGuid);
    } catch (error: any) {
      console.error("Error assigning menu item to group", error);
      showNotification("error", "Error", error?.response?.data?.message || "Failed to assign menu item to group");
    }
  };

  const handleRemoveMenuItem = async (menuItemGuid: string) => {
    try {
      await removeMenuOptionGroupFromMenu({
        menuItemId: menuItemGuid,
        menuOptionGroupGuid: selectedMogGuid,
      });
      showNotification("success", "Success", "Menu Item unassigned successfully");
      await bindMenuIemsForGroup(selectedMogGuid);
    } catch (error) {
      console.error("Error unassigning menu item from group", error);
      showNotification("error", "Error", "Failed to unassign menu item from group");
    }
  };

  const handleFilterGroupList = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setMenuOptionGroupsFilter(e.target.value);
  };

  const modifierOptions = [
    { label: 'None', value: '' },
    { label: 'Quantity', value: 'quantity' },
    { label: 'Portion', value: 'portion' },
  ];

  return (
    <>
      <Layout className="full-height-layout" style={{ backgroundColor: '#ffffff', height: 'var(--content-height, 100vh)' }}>
        <Splitter
          style={{
            height: '100%', // Adjust height as needed
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden'
          }}
        >
          <Splitter.Panel defaultSize="20%" min="20%" max="70%" >
            <Row>
              <Col span={2}>
              </Col>
              <Col span={20}>
                <Space direction="vertical">
                  <Flex vertical>
                    <h4>Option Groups</h4>
                      <Button color='green'
                        variant='solid'
                        size='small'
                        style={{ width: '50%' }}
                        onClick={resetGroupProperties}
                      >Add Group</Button>
                  </Flex>
                  <Flex vertical>
                    {isLoadingGroups ? (<Skeleton active />) : (<></>)}
                    <Space direction="vertical">
                      <Input value={menuOptionGroupsFilter} onChange={handleFilterGroupList} placeholder='Filter Groups' allowClear />
                      <List
                        style={{
                          backgroundColor: '#ededed'
                        }}
                        bordered
                        dataSource={menuOptionGroupsFiltered}
                        renderItem={(item) => (
                          <List.Item key={item.guid} onClick={() => handleClickLoadMog(item.guid)} >
                            {item.name}
                          </List.Item>
                        )}
                      />
                    </Space>
                  </Flex>
                </Space>
              </Col>
              <Col span={2}>
              </Col>
            </Row>
          </Splitter.Panel>
          <Splitter.Panel>
            {isShowMenuOptionGroupProperties === false &&
              <div style={{ padding: '20px' }}>Please select an Option Group or "Add Group" to continue</div>
            }
            {isShowMenuOptionGroupProperties === true &&
              <>
                <MenuOptionPropertyEditor
                  selectedMogGuid={selectedMogGuid}
                  selectedMogName={selectedMogName}
                  selectedMogDisplayName={selectedMogDisplayName}
                  selectedMogMinSelect={selectedMogMinSelect ?? 0}
                  selectedMogMaxSelect={selectedMogMaxSelect ?? 0}
                  selectedMogModifier={selectedMogModifier}
                  modifierOptions={modifierOptions}
                  isWarnGroupName={isWarnGroupName}
                  isWarnGroupDisplayName={isWarnGroupDisplayName}
                  inputRefGroupName={inputRefGroupName}
                  handleMogNameChange={handleMogNameChange}
                  handleMogDisplayNameChange={handleMogDisplayNameChange}
                  handleChangeMinSelect={handleChangeMinSelect}
                  handleChangeMaxSelect={handleChangeMaxSelect}
                  handleChangeModifier={handleChangeModifier}
                  handleSaveMenuOptionGroup={handleSaveMenuOptionGroup}
                  handleDeleteMenuOptionGroup={handleDeleteMenuOptionGroup}
                  selectedMogIsOnSale={selectedMogIsOnSale}
                  handleChangeIsOnSale={(checked) => setSelectedMogIsOnSale(checked)}
                />
                <MenuOptionGroupOptions
                  selectedMogOptions={selectedMogOptions.map(opt => ({
                    ...opt,
                    defaultSelection: opt.defaultSelection === true || opt.defaultSelection === "true",
                  }))}
                  columnsSort={columnsSort}
                  sensors={sensors}
                  handleDragEnd={handleDragEnd}
                  isAddOptionButtonDisabled={isAddOptionButtonDisabled}
                  handleNewMenuOption={handleNewMenuOption}
                  DragRow={DragRow}
                  stores={stores}
                  selectedStoreGuid={selectedStoreGuid}
                  onStoreChange={(storeGuid) => {
                    setSelectedStoreGuid(storeGuid);
                    if (selectedMogGuid) {
                      bindOptionsTable(selectedMogGuid, storeGuid);
                    }
                  }}

                />
                 <MenuItemsForGroup 
                  menuItemsForGroup={menuItemsForGroup} 
                  onAddMenuItem={handleAddMenuItem}
                  onRemoveMenuItem={handleRemoveMenuItem}
                 />
              </>}
          </Splitter.Panel>
        </Splitter>
      </Layout>
      <MenuOptionEditorDrawer
        open={openEditMogmol}
        onClose={onEditDrawerClose}
        menuOptionsDropDown={menuOptionsDropDown}
        selectedMenuOptionGuid={selectedMenuOptionGuid}
        handleOptionChange={handleOptionChange}
        isWarnMenuOption={isWarnMenuOption}
        selectedOptionDisplayName={selectedOptionDisplayName}
        handleOptionDisplayTextChange={handleOptionDisplayTextChange}
        isWarnOptionDisplayName={isWarnOptionDisplayName}
        selectedOptionDefaultSelection={selectedOptionDefaultSelection}
        selectedOptionStartDate={selectedOptionStartDate ? dayjs(selectedOptionStartDate) : null}
        selectedOptionEndDate={selectedOptionEndDate ? dayjs(selectedOptionEndDate) : null}
        handleDefaultOptionChange={handleDefaultOptionChange}
        handleOptionStartDateChange={handleOptionStartDateChange}
        handleOptionEndDateChange={handleOptionEndDateChange}
        handleSaveOptionLink={handleSaveOptionLink}
        handleCancelOptionLink={handleCancelOptionLink}
      />
    </>
  )
}
